import { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Alert,
} from 'reactstrap';
import { useCreateTaxRate, useUpdateTaxRate } from './useTaxRates';

/**
 * Create/Edit-Modal für einen Tax-Rate-Override.
 *
 * Modi:
 *  - Edit (existierender Override oder Default mit ISO fixed):
 *      → PUT /v1/admin/tax-rates/:country
 *  - Create (neues Drittland):
 *      → POST /v1/admin/tax-rates
 *
 * Soft-Warning lokal bei Out-of-Range (analog zum Core-Plausibility-Check),
 * Hard-Validation passiert serverseitig.
 */
export function TaxRateModal({ row, mode, onClose }) {
  const isCreate = mode === 'create';
  const create = useCreateTaxRate();
  const update = useUpdateTaxRate();

  const [form, setForm] = useState(() => ({
    country: isCreate ? '' : row.country,
    standard: row?.standard ?? '',
    reduced: row?.reduced ?? '',
    ossEligible: row?.ossEligible ?? false,
    reason: row?.reason ?? '',
  }));
  const [error, setError] = useState(null);

  const busy = create.isPending || update.isPending;

  const localWarnings = useMemo(() => {
    const w = [];
    const s = Number(form.standard);
    const r = Number(form.reduced);
    if (form.standard !== '' && (s < 5 || s > 30)) {
      w.push(`Standard ${s}% liegt außerhalb des typischen EU-Range (5–30%).`);
    }
    if (form.reduced !== '' && (r < 0 || r > 20)) {
      w.push(`Reduced ${r}% liegt außerhalb des typischen Range (0–20%).`);
    }
    if (form.standard !== '' && form.reduced !== '' && r > s) {
      w.push('Reduced > Standard — Server wird das blockieren.');
    }
    return w;
  }, [form.standard, form.reduced]);

  const setField = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) {
      setError('Grund ist Pflichtfeld (Audit-Log).');
      return;
    }
    if (form.standard === '') {
      setError('Standard-Rate ist Pflichtfeld.');
      return;
    }
    const body = {
      standard: Number(form.standard),
      reduced: form.reduced === '' ? null : Number(form.reduced),
      ossEligible: !!form.ossEligible,
      reason: form.reason.trim(),
    };
    try {
      if (isCreate) {
        await create.mutateAsync({ country: form.country.trim().toUpperCase(), ...body });
      } else {
        await update.mutateAsync({ country: row.country, ...body });
      }
      onClose();
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} backdrop="static">
      <Form onSubmit={onSubmit}>
        <ModalHeader toggle={busy ? undefined : onClose}>
          {isCreate ? 'Neuer Tax-Rate-Override' : `Tax-Rate für ${row.country}`}
        </ModalHeader>
        <ModalBody>
          {isCreate && (
            <FormGroup>
              <Label>Country (ISO-2)</Label>
              <Input
                type="text"
                value={form.country}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, country: e.target.value.toUpperCase().slice(0, 2) }))
                }
                placeholder="z.B. CH, NO, UK"
                maxLength={2}
                disabled={busy}
                style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
              />
              <div className="small text-muted">
                EL wird serverseitig zu GR normalisiert.
              </div>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Standard-Rate *</Label>
            <InputGroup>
              <Input
                type="number"
                value={form.standard}
                onChange={setField('standard')}
                step="0.1"
                min={0}
                max={50}
                disabled={busy}
              />
              <InputGroupText>%</InputGroupText>
            </InputGroup>
          </FormGroup>

          <FormGroup>
            <Label>Reduced-Rate (optional)</Label>
            <InputGroup>
              <Input
                type="number"
                value={form.reduced}
                onChange={setField('reduced')}
                step="0.1"
                min={0}
                max={50}
                disabled={busy}
                placeholder="leer = keine reduced-Rate"
              />
              <InputGroupText>%</InputGroupText>
            </InputGroup>
          </FormGroup>

          <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="oss-eligible"
              checked={!!form.ossEligible}
              onChange={setField('ossEligible')}
              disabled={busy}
            />
            <Label check for="oss-eligible">OSS-eligible</Label>
            <div className="small text-muted">
              Für EU-Mitgliedsländer normalerweise immer an. Bei Drittländern aus.
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Grund *</Label>
            <Input
              type="textarea"
              rows={2}
              value={form.reason}
              onChange={setField('reason')}
              placeholder="z. B. Buchpreisbindung § 12 Abs. 2 Nr. 1 UStG"
              disabled={busy}
              required
            />
            <div className="small text-muted">
              Pflichtfeld — landet im Audit-Log.
            </div>
          </FormGroup>

          {localWarnings.length > 0 && (
            <Alert color="warning" className="small mb-0">
              <strong>Plausibility-Warnung:</strong>
              <ul className="mb-0 ps-3">
                {localWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
              Server lässt Soft-Warnings durch; Hard-Block bei
              Werten außerhalb [0,50] oder reduced &gt; standard.
            </Alert>
          )}

          {error && <Alert color="danger" className="mt-2 mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={busy}>
            {busy ? 'Speichern…' : isCreate ? 'Anlegen' : 'Speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
